import { api } from '../../platform/index.ts';
import type { Capabilities } from '../../identity/index.ts';
import { unwrap } from '../../identity/index.ts';

/** What the New Project dialog collects: the workspace module's createProject body. */
export interface NewProjectInput {
  org: string;
  slug: string;
  name: string;
  visibility: 'private' | 'org';
}

/** A new project's slug, as the workspace module accepts it (its design database name derives from it). */
export const NEW_PROJECT_SLUG = /^[a-z][a-z0-9_-]{1,59}$/;

/** The slug the New Project dialog suggests for a name (the caller may still type another). */
export function projectSlugFor(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60);
}

/** Whether an organization of GET /api/capabilities lets the caller create projects in it. */
export function orgCreatesProjects(o: { capabilities: readonly string[] }): boolean {
  return o.capabilities.includes('project.create');
}

/** Whether the caller may create a project in `org`: a platform admin anywhere, else the org's capability. */
export function canCreateProjectIn(caps: Capabilities | null, org: string): boolean {
  if (!caps || !org) return false;
  return caps.platformAdmin || caps.orgs.some((o) => o.slug === org && orgCreatesProjects(o));
}

/**
 * The one place the web app creates a project: the workspace module's createProject, which records
 * the project and creates its design database (org admins only). Returns the new project's slug.
 */
export async function createProject(input: NewProjectInput): Promise<string> {
  const project = await unwrap(api.POST('/api/workspace/projects', { body: input }));
  return project.slug;
}
